/**
 * Type lists, custom types and active patterns for the current workspace.
 * DB-first read cache (migration 918); writes go through typePatternsService.
 */

import { create } from 'zustand';
import {
  fetchActivePatterns,
  fetchCustomTypes,
  fetchTypeLists,
} from '../../lib/typePatternsService';
import type { TypeList, TypeOption, TypePattern } from '../../types/prioritization/typePatterns';

interface TypePatternsState {
  workspaceId: string | null;
  lists: TypeList[];
  customTypes: Array<TypeOption & { listId: string }>;
  patterns: TypePattern[];
  loading: boolean;
  error: string | null;
  load: (workspaceId: string, opts?: { force?: boolean }) => Promise<void>;
  /** The active pattern for a type, or null. */
  patternFor: (listKey: string, typeValue: string) => TypePattern | null;
  /** Replace one active pattern after a revision. */
  putPattern: (pattern: TypePattern) => void;
  putList: (list: TypeList) => void;
  putCustomType: (type: TypeOption & { listId: string }) => void;
  clear: () => void;
}

export const useTypePatternsStore = create<TypePatternsState>()((set, get) => ({
  workspaceId: null,
  lists: [],
  customTypes: [],
  patterns: [],
  loading: false,
  error: null,

  load: async (workspaceId, opts) => {
    const s = get();
    if (!opts?.force && s.workspaceId === workspaceId && s.lists.length > 0) return;
    set({ loading: true, error: null, workspaceId });
    try {
      const [lists, customTypes, patterns] = await Promise.all([
        fetchTypeLists(workspaceId),
        fetchCustomTypes(workspaceId),
        fetchActivePatterns(workspaceId),
      ]);
      if (get().workspaceId !== workspaceId) return;
      set({ lists, customTypes, patterns, loading: false });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  patternFor: (listKey, typeValue) =>
    get().patterns.find((p) => p.listKey === listKey && p.typeValue === typeValue) ?? null,

  putPattern: (pattern) =>
    set((s) => ({
      patterns: [
        ...s.patterns.filter((p) => !(p.listKey === pattern.listKey && p.typeValue === pattern.typeValue)),
        pattern,
      ],
    })),

  putList: (list) =>
    set((s) => ({
      lists: [...s.lists.filter((l) => l.id !== list.id), list].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      ),
    })),

  putCustomType: (type) =>
    set((s) => ({ customTypes: [...s.customTypes.filter((t) => t.id !== type.id), type] })),

  clear: () => set({ workspaceId: null, lists: [], customTypes: [], patterns: [], loading: false, error: null }),
}));
